import { useMemo } from 'react';
import { FormConditionalService } from '@client/services/FormConditionalService';
import { FormParameterValues } from '@client/hooks/useFormParameters';

/**
 * Hook that uses the centralized FormConditionalService to get visible sections
 * This replaces the useVisibleSections function in useConditionalFormValidation.ts
 */
export function useVisibleFormSections(
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): Array<{ section: PolicyFormSection; subsections: PolicyFormSubSection[] }> {
  const conditionalService = useMemo(
    () => new FormConditionalService(formDefinition, formData, parameters),
    [formDefinition, formData, parameters],
  );

  return useMemo(() => {
    return conditionalService.getVisibleSections();
  }, [conditionalService]);
}

/**
 * Hook that calculates visible section and subsection indices
 * Converts absolute indices to indices within the visible sections array
 */
export function useVisibleSectionIndices(
  currentSection: PolicyFormSection | null | undefined,
  currentSubsection: PolicyFormSubSection | null | undefined,
  visibleSections: Array<{ section: PolicyFormSection; subsections: PolicyFormSubSection[] }>,
): {
  visibleSectionIndex: number;
  visibleSubsectionIndex: number;
} {
  return useMemo(() => {
    // Calculate visible section index
    const visibleSectionIndex = currentSection
      ? visibleSections.findIndex(data => data.section.id === currentSection.id)
      : -1;

    // Calculate visible subsection index within the current visible section
    let visibleSubsectionIndex = 0;
    if (currentSection && currentSubsection && visibleSectionIndex >= 0) {
      const visibleSection = visibleSections[visibleSectionIndex];
      if (visibleSection) {
        const subIndex = visibleSection.subsections.findIndex(
          sub => sub.id === currentSubsection.id
        );
        visibleSubsectionIndex = subIndex >= 0 ? subIndex : 0;
      }
    }

    return {
      visibleSectionIndex: visibleSectionIndex >= 0 ? visibleSectionIndex : 0,
      visibleSubsectionIndex,
    };
  }, [currentSection, currentSubsection, visibleSections]);
}

/**
 * Hook to get form conditional service instance
 * This can be used by components that need to check visibility and required state of elements
 */
export function useFormConditionals(
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): FormConditionalService {
  return useMemo(
    () => new FormConditionalService(formDefinition, formData, parameters),
    [formDefinition, formData, parameters],
  );
}

/**
 * @deprecated Use useFormConditionals instead
 */
export function useFormVisibility(
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): FormConditionalService {
  return useFormConditionals(formDefinition, formData, parameters);
}

/**
 * Hook to check if a specific question is visible, required, and text state
 */
export function useQuestionState(
  question: PolicyFormQuestion | undefined,
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): { visible: boolean; required: boolean; showConditionalText: boolean } {
  const conditionalService = useFormConditionals(formDefinition, formData, parameters);

  return useMemo(() => {
    if (!question) return { visible: false, required: false, showConditionalText: false };
    return conditionalService.getQuestionState(question);
  }, [conditionalService, question]);
}

/**
 * @deprecated Use useQuestionState instead
 */
export function useQuestionVisibility(
  question: PolicyFormQuestion | undefined,
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): { visible: boolean; required: boolean; showConditionalText: boolean } {
  return useQuestionState(question, formDefinition, formData, parameters);
}

/**
 * Hook to get total count of visible subsections
 * Useful for progress calculations
 */
export function useVisibleSubsectionCount(
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): number {
  const conditionalService = useFormConditionals(formDefinition, formData, parameters);

  return useMemo(() => {
    return conditionalService.getTotalVisibleSubsections();
  }, [conditionalService]);
}

/**
 * Hook to get complete form validation state
 * This replaces useConditionalFormValidation entirely
 */
export function useFormValidation(
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): {
  isValid: boolean;
  missingRequiredFields: Array<{
    questionRef: string;
    question: PolicyFormQuestion;
    subsectionId: string;
    sectionId: string;
  }>;
  visibleQuestions: Set<string>;
  requiredQuestions: Set<string>;
} {
  const conditionalService = useFormConditionals(formDefinition, formData, parameters);

  return useMemo(() => {
    return conditionalService.getFormValidationState();
  }, [conditionalService]);
}

/**
 * Hook to check if a subsection is complete
 */
export function useSubsectionValidation(
  subsection: PolicyFormSubSection | undefined,
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): {
  isComplete: boolean;
  missingFields: Array<{
    questionRef: string;
    question: PolicyFormQuestion;
  }>;
  requiredQuestions: PolicyFormQuestion[];
} {
  const conditionalService = useFormConditionals(formDefinition, formData, parameters);

  return useMemo(() => {
    if (!subsection) {
      return {
        isComplete: false,
        missingFields: [],
        requiredQuestions: [],
      };
    }

    const missingFields = conditionalService.getMissingRequiredFields(subsection);
    const requiredQuestions = conditionalService.getRequiredQuestionsForSubsection(subsection);

    return {
      isComplete: missingFields.length === 0,
      missingFields,
      requiredQuestions,
    };
  }, [conditionalService, subsection]);
}

/**
 * Hook to check if a question group is visible and get its visible questions
 */
export function useQuestionGroupState(
  group: PolicyFormQuestionGroup | undefined,
  formDefinition: PolicyFormDefinition,
  formData: PolicyFormSubmissionData,
  parameters: FormParameterValues = {},
): {
  isVisible: boolean;
  visibleQuestions: PolicyFormQuestion[];
  requiredQuestions: PolicyFormQuestion[];
} {
  const conditionalService = useFormConditionals(formDefinition, formData, parameters);

  return useMemo(() => {
    if (!group) {
      return {
        isVisible: false,
        visibleQuestions: [],
        requiredQuestions: [],
      };
    }

    const isVisible = conditionalService.isQuestionGroupVisible(group);
    const visibleQuestions: PolicyFormQuestion[] = [];
    const requiredQuestions: PolicyFormQuestion[] = [];

    if (isVisible) {
      for (const questionRef of group.questions) {
        const question = formDefinition.questions[questionRef];
        if (!question) continue;

        const state = conditionalService.getQuestionState(question);
        if (state.visible) {
          visibleQuestions.push(question);
          if (state.required) {
            requiredQuestions.push(question);
          }
        }
      }
    }

    return {
      isVisible,
      visibleQuestions,
      requiredQuestions,
    };
  }, [conditionalService, group, formDefinition]);
}